import React from 'react';
import { Section } from './_primitives/Section';
import { EmptyState } from './_primitives/EmptyState';
import type { KantoorRow } from './types';

interface Props {
  rows: KantoorRow[];
}

/**
 * Activity heatmap — one cell per kantoor, intensity scaled by 30-day render
 * volume. Cherry-picked from Nick Velten's vanilla admin reference
 * (basgosewisch/stagery-ai @ 7e6e63b). Rendered as a CSS grid (no chart lib).
 *
 * Falls back gracefully when the backend doesn't yet supply renders_30d /
 * active_users_7d / user_count / active_subs (today's `/api/admin/kantoren`
 * returns the user row only — UI shows 0s and a stripped subline).
 */
export const KantorenHeatmap: React.FC<Props> = ({ rows }) => {
  if (rows.length === 0) {
    return <Section title="Activiteit per kantoor"><EmptyState title="Geen kantoren" /></Section>;
  }

  const max = Math.max(...rows.map((r) => r.renders_30d ?? 0), 1);
  const hasRenderData = rows.some((r) => (r.renders_30d ?? 0) > 0);

  return (
    <Section title="Activiteit per kantoor" subtitle="Render-volume laatste 30 dagen — kleur = intensiteit">
      <div className="p-4 md:p-5">
        {!hasRenderData && (
          <p className="text-[11px] text-ink/45 dark:text-white/45 mb-3 italic">
            Render-statistieken zijn binnenkort beschikbaar.
          </p>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
          {rows.map((r) => {
            const r30 = r.renders_30d ?? 0;
            const intensity = r30 / max;
            const bg = `rgba(241,110,0,${(0.08 + intensity * 0.55).toFixed(2)})`;
            const borderClass = intensity > 0.5 ? 'border-accent/45' : 'border-black/[0.06] dark:border-white/10';
            return (
              <div
                key={r.id}
                className={`p-3 rounded-lg border ${borderClass} bg-clip-padding transition-transform hover:-translate-y-0.5`}
                style={{ background: bg }}
                title={r.email}
              >
                <div className="text-[12.5px] font-semibold text-ink dark:text-white truncate" title={r.company_name ?? r.email}>
                  {r.company_name ?? r.email}
                </div>
                <div className="text-2xl font-bold leading-none tracking-[-0.02em] text-ink dark:text-white mt-1.5">{r30}</div>
                <div className="text-[10.5px] text-ink/65 dark:text-white/70 mt-1">
                  {(r.active_users_7d ?? 0)}/{(r.user_count ?? 1)} actief (7d) · {(r.active_subs ?? 0)} abo
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
};
